import React, { useContext } from "react";
import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";
import { FavoritesContext } from "../contexts/FavoritesContext";
import { Link } from "react-router-dom";
import UserContext from "../contexts/userContext";

function QuickActions({ recipe, isFavorited, showActions }) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { handleFavorite, handleAddToList, handleShare } = useContext(FavoritesContext);

  if (!showActions) return null;

  const isDark = theme.palette.mode === "dark";
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  const glowColor = isDark
    ? "rgba(187, 134, 252, 0.2)"
    : "rgba(99, 102, 241, 0.15)";

  // Common button styling
  const buttonStyle = {
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "10px",
    color: accentPrimary,
    "&:hover": {
      background: `linear-gradient(135deg, ${accentPrimary}15, ${accentSecondary}10)`,
      transform: "scale(1.1)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  };

  const disabledStyle = {
    ...buttonStyle,
    color: theme.palette.action.disabled,
    opacity: 0.4,
    "&:hover": {
      background: "transparent",
      transform: "none",
    },
  };

  return (
    <Box
      className="quick-actions"
      sx={{
        flexDirection: "row",
        alignItems: "center",
        background: isMobile
          ? isDark
            ? "linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.97) 100%)"
          : "transparent",
        padding: isMobile ? theme.spacing(1) : theme.spacing(0.5),
        boxShadow: isMobile
          ? `0 -4px 20px -4px rgba(0,0,0,${isDark ? "0.4" : "0.15"})`
          : "none",
        borderLeft: isMobile
          ? `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`
          : "none",
        position: isMobile ? "absolute" : "static",
        top: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        height: isMobile ? "100%" : "auto",
        width: isMobile ? "208px" : "auto",
        display: showActions ? "flex" : "none",
        justifyContent: "space-around",
        gap: 0.5,
      }}
    >
      <IconButton
        className="quick-action-button"
        onClick={(e) => {
          e.stopPropagation();
          handleFavorite(e, recipe.id);
        }}
        sx={!user ? disabledStyle : {
          ...buttonStyle,
          color: isFavorited ? "#CF6679" : accentPrimary,
          "&:hover": {
            ...buttonStyle["&:hover"],
            boxShadow: isFavorited ? `0 0 12px -2px rgba(207, 102, 121, 0.4)` : `0 0 12px -2px ${glowColor}`,
          },
        }}
        aria-label={isFavorited ? "Unfavorite Recipe" : "Favorite Recipe"}
      >
        {isFavorited ? <FavoriteIcon /> : <FavoriteBorderIcon />}
      </IconButton>

      <IconButton
        className="quick-action-button"
        onClick={(e) => {
          e.stopPropagation();
          handleAddToList(e, recipe.id);
        }}
        sx={!user ? disabledStyle : buttonStyle}
        aria-label="Add Recipe to List"
      >
        <PlaylistAddIcon />
      </IconButton>

      <IconButton
        className="quick-action-button"
        onClick={(e) => {
          e.stopPropagation();
          handleShare(e, recipe.id);
        }}
        sx={disabledStyle}
        aria-label="Share Recipe"
      >
        <ShareIcon />
      </IconButton>

      {/* Edit Icon - Only shown if the user owns the recipe */}
      {user && (recipe.user_id === user.id || user.user_role === 'admin') && (
        <IconButton
          component={Link}
          to={`/recipes/edit/${recipe.id}`}
          className="quick-action-button"
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            ...buttonStyle,
            color: accentSecondary,
          }}
          aria-label="Edit Recipe"
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default QuickActions;